<template>
  <div class="ballot mt-3">
    <div class="d-flex justify-content-between align-items-center">
      <h4 class="mb-3">{{ $t("js.election_client.challenge.header") }}</h4>
      <button
        class="btn btn-lg btn-link border-0 p-0 me-2"
        :title="$t('js.election_client.challenge.verify_ballot')"
        :aria-label="$t('js.election_client.challenge.verify_ballot')"
        @click="openValidationModal"
      >
        <i class="fas fa-circle-question" aria-hidden="true"></i>
      </button>
    </div>

    <!-- Encrypt step feedback -->
    <div
      v-if="stepState.encrypt === 'active'"
      class="d-flex align-items-center gap-2 my-3"
    >
      <IndeterminateSpinner />
      <span>{{ $t("js.election_client.challenge.step.encrypting_ballot") }}</span>
    </div>
    <div
      v-if="stepState.encrypt === 'error'"
      class="alert alert-danger d-flex align-items-center gap-2 my-3"
      role="alert"
    >
      <span class="text-danger fw-bold fs-5">&#10007;</span>
      <span>{{ $t("js.election_client.challenge.step.validation_error") }}</span>
    </div>

    <!-- Validate step feedback -->
    <div
      v-if="stepState.validate === 'error'"
      class="alert alert-danger d-flex align-items-center gap-2 my-3"
      role="alert"
    >
      <span class="text-danger fw-bold fs-5">&#10007;</span>
      <span>{{ $t("js.election_client.challenge.step.validation_error") }}</span>
    </div>
    <div
      v-if="stepState.validate === 'active'"
      class="d-flex align-items-center gap-2 my-3"
    >
      <IndeterminateSpinner />
      <span>{{ $t("js.election_client.challenge.step.validating_ballot") }}</span>
    </div>
    <div
      v-if="stepsDone"
      class="alert alert-success d-flex align-items-center gap-2 my-3"
      role="alert"
    >
      <span class="text-success fw-bold fs-5">&#10003;</span>
      <span>{{ $t("js.election_client.challenge.step.validation_success") }}</span>
    </div>

    <!-- Ballot summary -->
    <BallotSummary
      v-for="contestSelection in ballotSelection.contestSelections"
      :key="contestSelection.reference"
      :contest-selection="contestSelection"
      :is-ballot-prefilled="isBallotPrefilled"
      @goto-selection="(contestReference: string) => $emit('goToContest', contestReference)"
    />

    <!-- Validation modal -->
    <Modal :visible="showValidationModal" @close="showValidationModal = false">
      <template #header>
        <h5 class="modal-title mb-0">
          {{ $t("js.election_client.challenge.validation_modal.title") }}
        </h5>
        <button
          type="button"
          class="btn-close"
          @click="showValidationModal = false"
          :aria-label="$t('js.election_client.challenge.validation_modal.close_modal')"
        ></button>
      </template>
      <template #body>
        <p
          class="mb-3"
          v-html="$t('js.election_client.challenge.validation_modal.description_html')"
        ></p>
      </template>
      <template #footer>
        <div class="d-flex gap-2 justify-content-end">
          <button class="btn btn-lg btn-secondary" @click="showValidationModal = false">
            {{ $t("js.election_client.challenge.validation_modal.cancel_button") }}
          </button>
          <button class="btn btn-lg btn-ballot" @click="startValidation">
            {{ $t("js.election_client.challenge.validation_modal.validate_button") }}
          </button>
        </div>
      </template>
    </Modal>

    <!-- Actions -->
    <div class="mt-3 d-flex flex-column flex-sm-row gap-2">
      <button
        class="btn btn-lg btn-outline-ballot full-width-mobile"
        @click="$emit('goBack')"
      >
        {{ $t("js.election_client.actions.back") }}
      </button>
      <span class="flex-grow-1"></span>
      <AsyncButton
        class="btn btn-lg btn-ballot full-width-mobile"
        :on-click="castBallot"
        :wait-time="100"
        :disabled="!canCastBallot"
      >
        <template #default>
          {{ $t("js.election_client.challenge.step.cast_ballot") }}
        </template>
        <template #waiting>
          <span class="hstack gap-2">
            <IndeterminateSpinner />
            <span>{{ $t("js.election_client.challenge.encrypting_and_casting_ballots") }}</span>
          </span>
        </template>
      </AsyncButton>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, computed } from "vue";
import { mapState } from "pinia";
import IndeterminateSpinner from "@/components/backend/live/IndeterminateSpinner.vue";
import AsyncButton from "@/components/shared/AsyncButton.vue";
import BallotSummary from "./BallotSummary.vue";
import Modal from "@/components/shared/Modal.vue";
import { useSharedStore } from "@/entrypoints/stores/shared";
import type { IAVClient, BallotSelection } from "@assemblyvoting/js-client/dist/lib/av_client/types";
import type { PropType } from "@/types";

type StepStatus = "pending" | "active" | "done" | "error";

export default defineComponent({
  name: "CanadianChallengeBallot",
  components: { IndeterminateSpinner, AsyncButton, BallotSummary, Modal },
  props: {
    client: {
      type: Object as PropType<IAVClient>,
      required: true,
    },
    ballotSelection: {
      type: Object as PropType<BallotSelection>,
      required: true,
    },
    onCastChosen: {
      type: Function,
      required: true,
    },
    isBallotPrefilled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["goBack", "goToContest", "ballotCodeUpdated"],
  data() {
    return {
      showValidationModal: false,
    };
  },
  setup() {
    const stepState = reactive<{ encrypt: StepStatus; validate: StepStatus }>({
      encrypt: "done", // parent already called constructBallot before mounting
      validate: "pending",
    });
    const stepsDone = computed(
      () => stepState.encrypt === "done" && stepState.validate === "done",
    );
    const canCastBallot = computed(
      () =>
        stepState.encrypt === "done" &&
        stepState.validate !== "error" &&
        stepState.validate !== "active",
    );
    return { stepState, stepsDone, canCastBallot };
  },
  computed: {
    ...mapState(useSharedStore, ["election"]),
  },
  methods: {
    openValidationModal() {
      this.stepState.validate = "pending";
      this.showValidationModal = true;
    },
    startValidation() {
      this.showValidationModal = false;
      this.runValidation();
    },
    async runValidation() {
      this.stepState.validate = "active";
      try {
        // spoil + reconstruct, mirrors election-client spoilAndReconstruct
        await this.client.spoilBallot();
        const newBallotCode = await this.client.constructBallot(this.ballotSelection);
        this.$emit("ballotCodeUpdated", newBallotCode);
        this.stepState.validate = "done";
      } catch (error) {
        console.error(error);
        this.stepState.validate = "error";
      }
    },
    async castBallot() {
      await this.onCastChosen();
    },
  },
});
</script>
